import { Link } from "react-router";
import { buildMetadata } from "@/lib/seo";

import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { Section } from "@/components/ui/Section";

/**
 * A wrong URL has to answer 404, not 200.
 *
 * Rendering the "not here" page while returning 200 is a soft 404: search
 * engines index the apology as a real page, and monitoring never sees the
 * broken link. Throwing here sets the response status, and the error boundary
 * renders this same component underneath it.
 */
export function loader() {
  throw new Response("Not found", { status: 404 });
}

export const meta = () =>
  buildMetadata({
    title: "Page not found",
    description: "The page you were looking for is not here.",
    path: "/404",
  });

const suggestions = [
  { label: "Our work", href: "/our-work", note: "The four programme pillars" },
  { label: "Impact", href: "/impact", note: "Documented figures and outcomes" },
  { label: "Stories", href: "/stories", note: "Reporting from the field" },
  { label: "Get involved", href: "/get-involved", note: "Partner, volunteer or trek" },
];

export function NotFoundPage() {
  return (
    <Section className="pt-32">
      <div className="max-w-2xl">
        <p className="eyebrow">404</p>
        <h1 className="mt-3" style={{ fontSize: "var(--step-h1)" }}>
          That page is not here
        </h1>
        <p className="mt-5 text-muted" style={{ fontSize: "var(--step-lead)" }}>
          The link may be out of date, or the page may have moved when this site was rebuilt. The
          main sections are below.
        </p>

        <ul className="mt-10 grid gap-4 sm:grid-cols-2">
          {suggestions.map((item) => (
            <li key={item.href}>
              <Link
                to={item.href}
                className="group block rounded-[14px] border border-line bg-white p-5 transition-colors hover:border-line-strong"
              >
                <span className="flex items-center gap-2 font-semibold text-navy">
                  {item.label}
                  <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
                <span className="mt-1 block text-[0.9375rem] text-muted">{item.note}</span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-10">
          <ButtonLink href="/">Back to the homepage</ButtonLink>
        </div>
      </div>
    </Section>
  );
}
